import { useCallback, useMemo, useState } from 'react';

import {
  applyDraftIntervals,
  createDraftSchedule,
} from '../model/workScheduleDrafts';

export const useWorkScheduleDrafts = ({ selectedScheduleGroup }) => {
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [workScheduleByPeriodSource, setWorkScheduleByPeriodSource] =
    useState(null);
  const [cachedChildSchedules, setCachedChildSchedules] = useState([]);

  const hasUnsavedDraft = cachedChildSchedules.some(
    (schedule) => schedule.isDraft
  );
  const selectedSchedules = useMemo(
    () => (selectedSchedule ? [selectedSchedule] : []),
    [selectedSchedule]
  );

  const removeCachedChildSchedule = useCallback((scheduleId) => {
    setCachedChildSchedules((prevState) =>
      prevState.filter((schedule) => schedule.id !== scheduleId)
    );
    setSelectedSchedule((prevSchedule) =>
      prevSchedule?.id === scheduleId ? null : prevSchedule
    );
  }, []);

  const createDraftWorkScheduleByPeriod = useCallback(
    (values) => {
      const currentDraft = workScheduleByPeriodSource?.isDraft
        ? workScheduleByPeriodSource
        : null;
      const draftSchedule = createDraftSchedule({
        id: currentDraft?.id ?? `draft-${Date.now()}`,
        selectedScheduleGroup,
        sourceSchedule: workScheduleByPeriodSource,
        values,
      });

      setCachedChildSchedules((prevState) =>
        currentDraft
          ? prevState.map((schedule) =>
              schedule.id === currentDraft.id ? draftSchedule : schedule
            )
          : [...prevState, draftSchedule]
      );
      setSelectedSchedule(draftSchedule);
      setWorkScheduleByPeriodSource(draftSchedule);

      return draftSchedule;
    },
    [selectedScheduleGroup, workScheduleByPeriodSource]
  );

  const updateCachedChildSchedule = useCallback((scheduleId, updater) => {
    const getNextSchedule = (schedule) =>
      typeof updater === 'function' ? updater(schedule) : updater;

    setCachedChildSchedules((prevState) =>
      prevState.map((schedule) =>
        schedule.id === scheduleId ? getNextSchedule(schedule) : schedule
      )
    );
    setSelectedSchedule((prevSchedule) =>
      prevSchedule?.id === scheduleId
        ? getNextSchedule(prevSchedule)
        : prevSchedule
    );
    setWorkScheduleByPeriodSource((prevSchedule) =>
      prevSchedule?.id === scheduleId
        ? getNextSchedule(prevSchedule)
        : prevSchedule
    );
  }, []);

  const assignDraftIntervals = useCallback(
    ({
      scheduleId,
      workerIds = [],
      dates = [],
      replaceDates = [],
      interval,
    }) => {
      if (
        !scheduleId ||
        !workerIds.length ||
        (!dates.length && !replaceDates.length) ||
        (dates.length && !interval)
      ) {
        return;
      }

      updateCachedChildSchedule(scheduleId, (schedule) =>
        applyDraftIntervals({
          dates,
          interval,
          replaceDates,
          schedule,
          workerIds,
        })
      );
    },
    [updateCachedChildSchedule]
  );

  return {
    assignDraftIntervals,
    cachedChildSchedules,
    createDraftWorkScheduleByPeriod,
    hasUnsavedDraft,
    removeCachedChildSchedule,
    selectedSchedule,
    selectedSchedules,
    setSelectedSchedule,
    setWorkScheduleByPeriodSource,
    workScheduleByPeriodSource,
  };
};
